import { useCallback, useMemo } from 'react';

import { FileModel, TagModel } from 'models';

import { PreparedFile, SortCriteria } from 'features/patient/fragments/PatientFiles.types';
// Adjust path if they are moved
import { getAllDescendantTagIds } from 'features/patient/services/tagHelperUtils';

import { ALL_FILES_TAG_ID, UNTAGGED_FILES_TAG_ID } from './useTagSidebarData';

// Assuming helpers moved here

interface UseFileFilteringResult {
  finalFilteredAndSortedFiles: PreparedFile[];
}

// Helper function to sort files (copied from PatientFiles)
const sortFiles = (filesToSort: PreparedFile[], criteria: SortCriteria): PreparedFile[] => {
  const sorted = [...filesToSort];
  switch (criteria) {
    case 'Oldest':
      sorted.sort((a, b) => a.sortableDate.valueOf() - b.sortableDate.valueOf());
      break;
    case 'Name':
      sorted.sort((a, b) => (a.fileName ?? '').localeCompare(b.fileName ?? ''));
      break;
    case 'Newest':
    default:
      sorted.sort((a, b) => b.sortableDate.valueOf() - a.sortableDate.valueOf());
      break;
  }
  return sorted;
};

export const useFileFiltering = (
  preparedFiles: PreparedFile[],
  filterTagIds: number[] | null,
  sortCriteria: SortCriteria,
  mergedHierarchicalTags: TagModel[], // Need the hierarchy for descendants
  mergedTagNameToIdMap: Map<string, number>,
  isLoadingMergedTags: boolean,
): UseFileFilteringResult => {
  // Filtering Logic
  const getFilteredPreparedFiles = useMemo(() => {
    console.log('[Filtering] Starting filter process in hook...');
    console.log('[Filtering] Filter Tag IDs:', filterTagIds);
    console.log('[Filtering] Total prepared files:', preparedFiles.length);

    // Handle null, empty array, or ALL_FILES_TAG_ID (no filter or show all)
    if (!filterTagIds || filterTagIds.length === 0 || filterTagIds.includes(ALL_FILES_TAG_ID)) {
      console.log('[Filtering] No specific tag filter or ALL_FILES_TAG_ID. Returning all prepared files.');
      return preparedFiles;
    }

    // Handle UNTAGGED_FILES_TAG_ID
    if (filterTagIds.includes(UNTAGGED_FILES_TAG_ID)) {
      console.log('[Filtering] Filtering for untagged files.');
      return preparedFiles.filter((file) => !file.tagIds || file.tagIds.length === 0);
    }

    // If tags are loading or unavailable, and a specific tag filter is applied
    // it's better to return an empty list than all files, as the user expects a filter.
    if (isLoadingMergedTags || mergedHierarchicalTags.length === 0) {
      console.log('[Filtering] Merged tags not ready for specific tag filtering, returning empty list.');
      return [];
    }

    // Proceed with filtering for specific tag IDs (and their descendants)
    console.log(`[Filtering] Applying specific tag filter for IDs:`, filterTagIds);

    const allSelectedAndDescendantIds = new Set<number>();
    filterTagIds.forEach((tagId) => {
      const descendantIds = getAllDescendantTagIds(tagId, mergedHierarchicalTags);
      descendantIds.forEach((id) => allSelectedAndDescendantIds.add(id));
    });

    console.log(`[Filtering] Including descendants, effective filter IDs:`, Array.from(allSelectedAndDescendantIds));

    if (allSelectedAndDescendantIds.size === 0) {
      console.log('[Filtering] No effective tag IDs after including descendants. Returning empty.');
      return [];
    }

    const tagFilteredFiles = preparedFiles.filter((file) => {
      return file.tagIds?.some((fileTagId) => allSelectedAndDescendantIds.has(fileTagId)) ?? false;
    });

    console.log(`[Filtering] Files after specific tag filter: ${tagFilteredFiles.length}`);
    return tagFilteredFiles;
  }, [preparedFiles, filterTagIds, mergedHierarchicalTags, isLoadingMergedTags, mergedTagNameToIdMap]);

  // Sorting Logic
  const finalFilteredAndSortedFiles = useMemo(() => {
    return sortFiles(getFilteredPreparedFiles, sortCriteria);
  }, [getFilteredPreparedFiles, sortCriteria]);

  return { finalFilteredAndSortedFiles };
};
